import React, { useMemo } from "react";

const OrganizationChart = ({
  employees,
  isOdooConfigured = false,
  hrmsSource = "none",
}) => {
  const isValidProfilePic = (base64) => {
    if (!base64 || typeof base64 !== "string") return false;

    return (
      base64.startsWith("/9j/") || // JPEG
      base64.startsWith("iVBOR") // PNG
    );
  };
  const layout = useMemo(() => {
    if (!employees?.length) {
      return { nodes: [], edges: [], totalWidth: 0, totalHeight: 0 };
    }

    const cardWidth = 280;
    const cardHeight = 140;
    const siblingGap = 50;
    const levelHeight = 220;

    const empMap = {};

    // ✅ FIXED DATA MAPPING WITH PROFILE PIC
    employees.forEach((emp) => {
      const id = emp.external_id;

      empMap[id] = {
        id,
        name: `${emp.firstname || ""} ${emp.lastname || ""}`.trim(),
        designation: emp.designation,
        department: emp.department,
        reporting_to_id: emp.reporting_to_id,
        profile_pic: emp.profile_pic,
        children: [],
      };
    });

    const roots = [];

    employees.forEach((emp) => {
      const id = emp.external_id;

      if (!emp.reporting_to_id) {
        roots.push(empMap[id]);
      } else if (empMap[emp.reporting_to_id]) {
        empMap[emp.reporting_to_id].children.push(empMap[id]);
      }
    });

    // 🌳 Calculate subtree width
    const calcWidth = (node) => {
      if (node.children.length === 0) {
        node.subtreeWidth = cardWidth;
        return cardWidth;
      }

      let total = 0;
      node.children.forEach((child, i) => {
        total += calcWidth(child);
        if (i > 0) total += siblingGap;
      });

      node.subtreeWidth = Math.max(cardWidth, total);
      return node.subtreeWidth;
    };

    let minX = Infinity;
    let maxX = -Infinity;
    let maxDepth = 0;

    // 📍 Set positions
    const setPositions = (node, x, depth) => {
      node.x = x;
      node.y = depth * levelHeight;

      minX = Math.min(minX, x - cardWidth / 2);
      maxX = Math.max(maxX, x + cardWidth / 2);
      maxDepth = Math.max(maxDepth, depth);

      let startX = x - node.subtreeWidth / 2;

      node.children.forEach((child) => {
        setPositions(child, startX + child.subtreeWidth / 2, depth + 1);
        startX += child.subtreeWidth + siblingGap;
      });
    };

    let currentX = 0;

    roots.forEach((root) => {
      calcWidth(root);
      setPositions(root, currentX + root.subtreeWidth / 2, 0);
      currentX += root.subtreeWidth + siblingGap * 2;
    });

    const padding = 80;
    const xOffset = -minX + padding;
    const yOffset = padding;

    const nodes = [];
    const edges = [];

    const traverse = (node) => {
      const x = node.x + xOffset;
      const y = node.y + yOffset;

      nodes.push({ ...node, x, y });

      node.children.forEach((child) => {
        const cx = child.x + xOffset;
        const cy = child.y + yOffset;

        edges.push({
          id: `${node.id}-${child.id}`,
          fromX: x,
          fromY: y + cardHeight,
          toX: cx,
          toY: cy,
        });

        traverse(child);
      });
    };

    roots.forEach(traverse);

    return {
      nodes,
      edges,
      totalWidth: maxX - minX + padding * 2,
      totalHeight: maxDepth * levelHeight + cardHeight + padding * 2,
      cardWidth,
      cardHeight,
    };
  }, [employees]);

  const getInitials = (name) =>
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 flex flex-col h-full">
      <div className="mb-6 pb-4 border-b border-gray-200">
        <h3 className="text-xl font-bold text-gray-900">
          Organization Hierarchy
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Reporting structure and team dependencies
        </p>
      </div>

      {hrmsSource === "none" ? (
        <div className="flex-grow flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border-2 border-dashed border-blue-200">
          <div className="text-center py-12 px-6">
            <div className="mb-4">
              <svg
                className="w-16 h-16 mx-auto text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Need to Integrate HRMS Tool
            </h3>
            <p className="text-gray-600 text-sm max-w-md mx-auto">
              Configure Odoo or any HRMS integration to view your organization
              hierarchy and reporting structure.
            </p>
            <p className="text-blue-600 text-xs font-medium mt-4">
              Configure HRMS through Asset Manager → Connectors
            </p>
          </div>
        </div>
      ) : employees?.length === 0 ? (
        <div className="flex-grow flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-center py-12 px-6">
            <svg
              className="w-12 h-12 mx-auto text-gray-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-base font-semibold text-gray-700 mb-1">
              No Employees Synced Yet
            </h3>
            <p className="text-gray-500 text-sm">
              Start syncing employees from your HRMS to build the organization
              hierarchy
            </p>
          </div>
        </div>
      ) : (
        <div className="relative w-full overflow-auto bg-gray-50 border border-gray-200 rounded-lg flex-grow shadow-sm">
          <div
            className="relative"
            style={{
              width: layout.totalWidth,
              height: layout.totalHeight,
            }}
          >
            {/* 🔗 SVG CONNECTION LINES */}
            <svg
              className="absolute inset-0 pointer-events-none"
              width="100%"
              height="100%"
              style={{ overflow: "visible" }}
              preserveAspectRatio="none"
            >
              {layout.edges.map((edge) => {
                const midY = edge.fromY + (edge.toY - edge.fromY) / 2;

                return (
                  <g key={edge.id}>
                    {/* Shadow line */}
                    <path
                      d={`M ${edge.fromX} ${edge.fromY} V ${midY} H ${edge.toX} V ${edge.toY}`}
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {/* Main gray line */}
                    <path
                      d={`M ${edge.fromX} ${edge.fromY} V ${midY} H ${edge.toX} V ${edge.toY}`}
                      fill="none"
                      stroke="#9ca3af"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                );
              })}
            </svg>

            {/* 🧑‍💼 NODES */}
            {layout.nodes.map((node) => (
              <div
                key={node.id}
                className="absolute group transition-all duration-300 hover:scale-105 hover:z-20"
                style={{
                  left: node.x - layout.cardWidth / 2,
                  top: node.y,
                }}
              >
                <div
                  className="bg-white border border-gray-200 rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden group-hover:border-blue-400"
                  style={{
                    width: layout.cardWidth,
                    height: layout.cardHeight,
                  }}
                >
                  {/* Top accent bar - blue */}
                  <div className="h-1 bg-blue-600" />

                  <div className="p-4 flex flex-col h-full">
                    {/* Avatar and Name Section */}
                    <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-100">
                      <div className="flex-shrink-0">
                        {isValidProfilePic(node?.profile_pic) ? (
                          <img
                            src={`data:image/png;base64,${node.profile_pic}`}
                            alt={node.name}
                            className="w-12 h-12 rounded-full object-cover ring-2 ring-gray-100"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm shadow-sm ring-2 ring-gray-100">
                            {getInitials(node.name)}
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-900 truncate">
                          {node.name}
                        </div>
                        <p className="text-xs font-semibold text-gray-500 truncate">
                          Team Member
                        </p>
                      </div>
                    </div>

                    {/* Info Section */}
                    <div className="flex-1 flex flex-col justify-between min-w-0">
                      <div className="space-y-2">
                        <div className="flex items-start gap-2 min-w-0">
                          <span className="text-gray-400 font-bold text-xs mt-0.5 flex-shrink-0">
                            💼
                          </span>
                          <div className="text-xs text-gray-700 truncate font-medium">
                            {node.designation || "—"}
                          </div>
                        </div>
                        <div className="flex items-start gap-2 min-w-0">
                          <span className="text-gray-400 font-bold text-xs mt-0.5 flex-shrink-0">
                            🏢
                          </span>
                          <div className="text-xs text-gray-600 truncate">
                            {node.department || "—"}
                          </div>
                        </div>
                      </div>

                      {/* Active status indicator */}
                      <div className="flex items-center gap-1 mt-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 shadow-sm" />
                        <span className="text-xs text-gray-400 font-medium">
                          Active
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationChart;
